import React, { useMemo } from "react";
import {
  Search,
  Filter,
  SlidersHorizontal,
  Download,
  Upload,
  Plus,
} from "lucide-react";

const TableToolbar = ({
  // Búsqueda
  searchTerm,
  setSearchTerm,
  searchPlaceholder = "Buscar...",

  // ✅ API nueva (opcional)
  dropdowns, // { filter: bool, columns: bool, export: bool }
  toggleDropdown, // (key) => void

  // ✅ API vieja (LA QUE TÚ ESTÁS USANDO EN TablaCotizaciones.jsx)
  showFilterDropdown,
  setShowFilterDropdown,
  showColumnConfig,
  setShowColumnConfig,
  showExportDropdown,
  setShowExportDropdown,

  // Columnas
  visibleColumns,
  setVisibleColumns,
  columnOptions,

  // Filtros
  filterStatus,
  setFilterStatus,
  filterOptions = ["Todos", "Pendiente", "Aprobado", "Rechazado"],

  // Acciones
  onAdd,
  onImport,
  onExport,
  addButtonTooltip = "Nuevo",

  // Refs
  fileInputRef,
}) => {
  // ✅ Si viene API nueva úsala, si no, usa la vieja
  const safeDropdowns = useMemo(() => {
    if (dropdowns) return dropdowns;
    return {
      filter: !!showFilterDropdown,
      columns: !!showColumnConfig,
      export: !!showExportDropdown,
    };
  }, [dropdowns, showFilterDropdown, showColumnConfig, showExportDropdown]);

  const toggleDropdownCompat = (key) => {
    // 1) Si existe la API nueva, úsala
    if (typeof toggleDropdown === "function") {
      toggleDropdown(key);
      return;
    }

    // 2) Si no, usa tu API vieja (setters)
    if (key === "filter" && typeof setShowFilterDropdown === "function") {
      setShowFilterDropdown((v) => !v);
      return;
    }
    if (key === "columns" && typeof setShowColumnConfig === "function") {
      setShowColumnConfig((v) => !v);
      return;
    }
    if (key === "export" && typeof setShowExportDropdown === "function") {
      setShowExportDropdown((v) => !v);
      return;
    }
  };

  const colList = useMemo(() => {
    if (Array.isArray(columnOptions) && columnOptions.length)
      return columnOptions;

    const defaultOrder = [
      { key: "Codigo", label: "Código" },
      { key: "Nombre", label: "Nombre" },
      { key: "Tipo", label: "Tipo" },
      { key: "Direccion", label: "Dirección" },
      { key: "Responsable", label: "Responsable" },
      { key: "Telefono", label: "Teléfono" },
      { key: "Correo", label: "Correo" },
      { key: "Estado", label: "Estado" },
      { key: "FechaCreacion", label: "Fecha de Creación" },
    ];

    const knownKeys = new Set(defaultOrder.map((x) => x.key));
    const extras = Object.keys(visibleColumns || {})
      .filter((k) => !knownKeys.has(k))
      .map((k) => ({ key: k, label: k }));

    return [...defaultOrder, ...extras];
  }, [columnOptions, visibleColumns]);

  const handleToggleColumn = (key) => {
    if (!setVisibleColumns) return;
    setVisibleColumns((p) => ({ ...p, [key]: !p?.[key] }));
  };

  const handleImportClick = () => {
    if (typeof onImport === "function") return onImport();
    fileInputRef?.current?.click?.();
  };

  const handleExportClick = () => {
    if (typeof onExport === "function") onExport();
  };

  return (
    <>
      <div className="toolbar-container">
        <div style={{ flexGrow: 1 }}>
          <div className="search-bar">
            <Search size={16} />
            <input
              type="text"
              className="search-input"
              placeholder={searchPlaceholder}
              value={searchTerm || ""}
              onChange={(e) => setSearchTerm?.(e.target.value)}
            />
          </div>
        </div>

        <div className="action-group">
          {/* FILTROS */}
          <button
            className="icon-button"
            data-dd="filter"
            data-tooltip-top="Filtros"
            onClick={(e) => {
              e.stopPropagation();
              toggleDropdownCompat("filter");
            }}
            style={{
              color: safeDropdowns.filter ? "#3B82F6" : "inherit",
              backgroundColor: safeDropdowns.filter ? "#eff6ff" : "transparent",
            }}
          >
            <Filter size={18} />
          </button>

          <div className="vertical-divider" />

          {/* COLUMNAS */}
          <div style={{ position: "relative" }}>
            <button
              className="icon-button"
              data-dd="columns"
              data-tooltip-top="Configurar Columnas"
              onClick={(e) => {
                e.stopPropagation();
                toggleDropdownCompat("columns");
              }}
              style={{
                color: safeDropdowns.columns ? "#3B82F6" : "inherit",
                backgroundColor: safeDropdowns.columns
                  ? "#eff6ff"
                  : "transparent",
              }}
            >
              <SlidersHorizontal size={18} />
            </button>

            {safeDropdowns.columns && (
              <div className="dropdown-custom column-config">
                <div
                  style={{
                    padding: "8px 12px 12px 12px",
                    fontSize: "14px",
                    fontWeight: "500",
                    color: "#111827",
                  }}
                >
                  Configurar Columnas
                </div>

                {colList.map(({ key, label }) => (
                  <div
                    key={key}
                    className="config-item"
                    onClick={() => handleToggleColumn(key)}
                    style={{
                      padding: "8px 12px",
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                      cursor: "pointer",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={!!visibleColumns?.[key]}
                      readOnly
                      className="checkbox-custom"
                    />
                    <span style={{ fontSize: "14px", color: "#374151" }}>
                      {label}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* EXPORTAR */}
          <div style={{ position: "relative" }}>
            <button
              className="icon-button"
              data-dd="export"
              data-tooltip-top="Exportar"
              onClick={(e) => {
                e.stopPropagation();
                toggleDropdownCompat("export");
              }}
              style={{
                color: safeDropdowns.export ? "#3B82F6" : "inherit",
                backgroundColor: safeDropdowns.export
                  ? "#eff6ff"
                  : "transparent",
              }}
            >
              <Download size={18} />
            </button>

            {safeDropdowns.export && (
              <div className="dropdown-custom export-menu">
                <button className="export-item" onClick={handleImportClick}>
                  <Upload size={18} strokeWidth={1.5} />
                  <span>Importar</span>
                </button>

                <button className="export-item" onClick={handleExportClick}>
                  <Download size={18} strokeWidth={1.5} />
                  <span>Exportar</span>
                </button>
              </div>
            )}
          </div>

          <div className="vertical-divider" />

          <button
            className="primary-button"
            data-tooltip-top={addButtonTooltip}
            onClick={onAdd}
          >
            <Plus size={18} />
          </button>
        </div>
      </div>

      {/* PANEL DE FILTROS */}
      {safeDropdowns.filter && (
        <div className="filter-panel">
          <div className="filter-group">
            <span className="filter-label">Estado:</span>
            {filterOptions.map((estado) => (
              <button
                key={estado}
                className={`filter-option ${
                  filterStatus === estado ? "active" : ""
                }`}
                onClick={() => setFilterStatus?.(estado)}
              >
                {estado}
              </button>
            ))}
          </div>
        </div>
      )}
    </>
  );
};

export default TableToolbar;
